import { z } from "zod";
import type { MediaAssetSlim } from "@/lib/media/types";
import { isSafeMediaKey } from "@/lib/security/user-paths";

/**
 * Questions an organiser asks per ticket at checkout ("Plus one, if any?").
 *
 * Name, email and the event-level booking fields (gender, birthday, photo,
 * location) are asked for every booking already; these are the extras that
 * only make sense for one ticket. Stored as JSON on `event_pass_type`, and
 * every answer is snapshotted onto the booking line together with the label it
 * answered, so editing or deleting a question never rewrites history.
 */
export const TICKET_QUESTION_KINDS = [
  "text",
  "long_text",
  "choice",
  "media",
] as const;
export type TicketQuestionKind = (typeof TICKET_QUESTION_KINDS)[number];

export const MAX_TICKET_QUESTIONS = 10;
export const MAX_TICKET_QUESTION_LABEL = 200;
export const MAX_TICKET_ANSWER_LENGTH = 1000;
const MAX_CHOICE_OPTIONS = 12;
export const MAX_TICKET_MEDIA_FILES = 10;
const MAX_TICKET_MEDIA_ANSWER_LENGTH = MAX_TICKET_MEDIA_FILES * 2000;
export const MAX_TICKET_MEDIA_BYTES = 20 * 1024 * 1024;

const mediaKeySchema = z.string().max(600).refine(isSafeMediaKey);
const ticketMediaInputSchema = z
  .object({
    id: z.string().regex(/^[a-f0-9]{32}$/),
    key: mediaKeySchema,
    order: z.literal(0),
    posterKey: mediaKeySchema.optional(),
    hlsKey: mediaKeySchema.optional(),
  })
  .strict();

/** Compact upload reference; the server resolves the actual asset by owner and id. */
export function encodeTicketMediaAnswer(asset: MediaAssetSlim): string {
  return JSON.stringify({
    id: asset.id,
    key: asset.key,
    order: 0,
    ...(asset.posterKey ? { posterKey: asset.posterKey } : {}),
    ...(asset.hlsKey ? { hlsKey: asset.hlsKey } : {}),
  });
}

export function readTicketMediaInput(value: string): MediaAssetSlim | null {
  const parsed = ticketMediaInputSchema.safeParse(safeJsonParse(value));
  return parsed.success ? parsed.data : null;
}

/** Accept legacy single-file references as well as ordered multi-file answers. */
export function readTicketMediaInputs(value: string): MediaAssetSlim[] | null {
  const raw = safeJsonParse(value);
  const parsed = z
    .array(ticketMediaInputSchema)
    .min(1)
    .max(MAX_TICKET_MEDIA_FILES)
    .safeParse(Array.isArray(raw) ? raw : [raw]);
  if (
    !parsed.success ||
    new Set(parsed.data.map((asset) => asset.id)).size !== parsed.data.length
  )
    return null;
  return parsed.data;
}

export function encodeTicketMediaAnswers(assets: MediaAssetSlim[]): string {
  if (!assets.length) return "";
  if (assets.length === 1) return encodeTicketMediaAnswer(assets[0]);
  return JSON.stringify(
    assets.map((asset) => JSON.parse(encodeTicketMediaAnswer(asset))),
  );
}

export type TicketQuestion = {
  id: string;
  label: string;
  kind: TicketQuestionKind;
  required: boolean;
  /** Only for `choice`. */
  options?: string[];
  /** Maximum uploads for a media question; older questions allow one. */
  maxFiles?: number;
  helpText?: string | null;
};

export type TicketAnswer = {
  questionId: string;
  label: string;
  answer: string;
  /** Snapshotted so uploads remain recognisable after a question is edited. */
  kind?: "media";
  media?: MediaAssetSlim;
  mediaFiles?: MediaAssetSlim[];
};

const questionIdSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9_-]{1,40}$/i, "Question id is invalid");

const ticketQuestionFields = {
  label: z
    .string()
    .trim()
    .min(1, "Every question needs a label")
    .max(
      MAX_TICKET_QUESTION_LABEL,
      `Questions must be under ${MAX_TICKET_QUESTION_LABEL} characters`,
    ),
  kind: z.enum(TICKET_QUESTION_KINDS),
  required: z.boolean(),
  options: z
    .array(z.string().trim().min(1).max(80))
    .max(MAX_CHOICE_OPTIONS)
    .optional(),
  maxFiles: z.number().int().min(1).max(MAX_TICKET_MEDIA_FILES).optional(),
  helpText: z.string().trim().max(200).nullish(),
};

function refineChoiceOptions(
  question: { label: string; kind: TicketQuestionKind; options?: string[] },
  ctx: z.RefinementCtx,
) {
  if (question.kind !== "choice") return;
  const options = question.options ?? [];
  if (options.length < 2) {
    ctx.addIssue({
      code: "custom",
      path: ["options"],
      message: `"${question.label}" needs at least two options`,
    });
  }
  if (
    new Set(options.map((option) => option.toLowerCase())).size !==
    options.length
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["options"],
      message: `"${question.label}" has duplicate options`,
    });
  }
}

export const ticketQuestionSchema = z
  .object({ id: questionIdSchema, ...ticketQuestionFields })
  .strict()
  .superRefine(refineChoiceOptions);

/** The same question before it has an id (MCP proposals). */
export const ticketQuestionDraftSchema = z
  .object({ id: questionIdSchema.optional(), ...ticketQuestionFields })
  .strict()
  .superRefine(refineChoiceOptions);

export const ticketQuestionsSchema = z
  .array(ticketQuestionSchema)
  .max(
    MAX_TICKET_QUESTIONS,
    `A ticket can ask up to ${MAX_TICKET_QUESTIONS} questions`,
  )
  .superRefine((questions, ctx) => {
    const ids = questions.map((question) => question.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: "custom",
        message: "Each question needs a unique id",
      });
    }
  });

/** What the booker sends: question id → answer. */
export const ticketAnswersInputSchema = z
  .record(
    questionIdSchema,
    z.string().max(MAX_TICKET_MEDIA_ANSWER_LENGTH, "Answers must be shorter"),
  )
  .optional();

/** Tolerant read of the stored column: a malformed row asks nothing. */
export function readTicketQuestions(raw: unknown): TicketQuestion[] {
  const value = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!Array.isArray(value)) return [];
  const parsed = ticketQuestionsSchema.safeParse(value);
  return parsed.success ? normalizeTicketQuestions(parsed.data) : [];
}

/** Drops options from non-choice questions so the stored shape stays tidy. */
export function normalizeTicketQuestions(
  questions: TicketQuestion[],
): TicketQuestion[] {
  return questions.map((question) => ({
    id: question.id,
    label: question.label.trim(),
    kind: question.kind,
    required: question.required,
    ...(question.kind === "choice"
      ? { options: (question.options ?? []).map((option) => option.trim()) }
      : {}),
    ...(question.kind === "media" && question.maxFiles !== undefined
      ? { maxFiles: question.maxFiles }
      : {}),
    ...(question.helpText?.trim()
      ? { helpText: question.helpText.trim() }
      : {}),
  }));
}

export function newTicketQuestionId() {
  return `q_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The server's ruling on what a booker typed. Unknown ids are ignored (the
 * question may have been deleted while they were on the page), required
 * questions must be answered, and a choice must be one of the options as they
 * stand now.
 */
export function resolveTicketAnswers(
  questions: TicketQuestion[],
  input: Record<string, string> | undefined,
  ticketName: string,
): { ok: true; answers: TicketAnswer[] } | { ok: false; message: string } {
  const answers: TicketAnswer[] = [];
  for (const question of questions) {
    const raw = input?.[question.id];
    const answer = typeof raw === "string" ? raw.trim() : "";
    if (!answer) {
      if (question.required) {
        return {
          ok: false,
          message: `Answer "${question.label}" for ${ticketName}.`,
        };
      }
      continue;
    }
    if (
      answer.length >
      (question.kind === "media"
        ? MAX_TICKET_MEDIA_ANSWER_LENGTH
        : MAX_TICKET_ANSWER_LENGTH)
    ) {
      return {
        ok: false,
        message: `Your answer to "${question.label}" is too long.`,
      };
    }
    if (question.kind === "media") {
      const files = readTicketMediaInputs(answer);
      if (!files || files.length > (question.maxFiles ?? 1)) {
        return {
          ok: false,
          message: `Upload up to ${question.maxFiles ?? 1} photo(s) or video(s) for "${question.label}".`,
        };
      }
      answers.push({
        questionId: question.id,
        label: question.label,
        answer,
        kind: "media",
      });
      continue;
    }
    if (question.kind === "choice") {
      const option = (question.options ?? []).find(
        (candidate) => candidate.toLowerCase() === answer.toLowerCase(),
      );
      if (!option) {
        return {
          ok: false,
          message: `Pick one of the options for "${question.label}".`,
        };
      }
      answers.push({
        questionId: question.id,
        label: question.label,
        answer: option,
      });
      continue;
    }
    answers.push({ questionId: question.id, label: question.label, answer });
  }
  return { ok: true, answers };
}

/** Answers stored on `event_booking_pass.metadata`, read tolerantly. */
export function readTicketAnswers(metadata: unknown): TicketAnswer[] {
  const value =
    typeof metadata === "string" ? safeJsonParse(metadata) : metadata;
  if (!value || typeof value !== "object") return [];
  const answers = (value as { answers?: unknown }).answers;
  if (!Array.isArray(answers)) return [];
  return answers.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const { questionId, label, answer, kind, media, mediaFiles } =
      entry as Record<string, unknown>;
    const storedFiles =
      kind === "media" && Array.isArray(mediaFiles)
        ? readTicketMediaInputs(
            JSON.stringify(
              mediaFiles.map((file) =>
                file && typeof file === "object"
                  ? JSON.parse(encodeTicketMediaAnswer(file as MediaAssetSlim))
                  : null,
              ),
            ),
          )
        : null;
    const mediaAsset =
      kind === "media" && media
        ? readTicketMediaInput(encodeTicketMediaAnswer(media as MediaAssetSlim))
        : null;
    return typeof questionId === "string" &&
      typeof label === "string" &&
      typeof answer === "string"
      ? [
          {
            questionId,
            label,
            answer,
            ...(storedFiles
              ? { kind: "media" as const, mediaFiles: storedFiles }
              : mediaAsset
                ? { kind: "media" as const, media: mediaAsset }
                : {}),
          },
        ]
      : [];
  });
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Questions authored without ids (an MCP proposal, say) get stable ones: an
 * existing question with the same label keeps its id, so answers already on
 * bookings stay attached to it.
 */
export function assignTicketQuestionIds(
  questions: Array<Omit<TicketQuestion, "id"> & { id?: string }>,
  existing: TicketQuestion[],
): TicketQuestion[] {
  const used = new Set<string>();
  return questions.map((question, index) => {
    const match = existing.find(
      (candidate) =>
        !used.has(candidate.id) &&
        candidate.label.trim().toLowerCase() ===
          question.label.trim().toLowerCase(),
    );
    let id = question.id ?? match?.id ?? `q${index + 1}`;
    while (used.has(id)) id = `${id}_${index + 1}`;
    used.add(id);
    return { ...question, id };
  });
}
